import { yupResolver } from '@hookform/resolvers/yup';
import { Col, Grid, message, Row, Spin, Table } from 'antd';
import {
  PermissionsEnum,
  useDeleteRegionMutation,
  useGetRegionsQuery,
  useUpdateRegionMutation,
} from 'gql/types';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getColumns } from './columns';
import {
  IUpdateRegionsFormSchema,
  updateRegionsFormSchema as updateRegionsFormSchema,
} from './schema';
import styles from './styles.module.scss';

import CreateRegionsForm from '../CreateRegionForm';

const updateRegionsDefaultValues = {
  id: undefined,
  name: undefined,
};

const RegionsTable: FC = () => {
  const { t } = useTranslation('user.LocationsTable');
  const { xs } = Grid.useBreakpoint();
  const [isMobile, setMobile] = useState<boolean>(false);
  const pagination = usePagination();
  const currentUser = useCurrentUser();
  const [regionName, setRegionName] = useState<string | undefined>();
  const [idexxAccountNumber, setIdexxAccountNumber] = useState<
    string | undefined
  >();
  const [zoetisAccountNumber, setZoetisAccountNumber] = useState<
    string | undefined
  >();

  const createNewRegionAllowed = currentUser.isCan(
    PermissionsEnum.CreteLocation
  );
  const deleteRegionAllowed = currentUser.isCan(PermissionsEnum.DeleteLocation);
  const updateRegionAllowed = currentUser.isCan(PermissionsEnum.UpdateLocation);

  useEffect(() => {
    xs !== undefined && setMobile(xs);
  }, [xs]);

  const [deleteRegion, deleteRegionMutation] = useDeleteRegionMutation({
    refetchQueries: ['getRegions'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      message.success(t('locationDeleted'));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
  const [updateRegion, updateRegionMutation] = useUpdateRegionMutation({
    refetchQueries: ['getRegions'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      currentUser.refetchData();
      message.success(t('locationUpdated'));
      updateRegionsForm.reset();
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
  const updateRegionsForm = useForm<IUpdateRegionsFormSchema>({
    resolver: yupResolver(updateRegionsFormSchema),
    mode: 'onChange',
    defaultValues: updateRegionsDefaultValues,
  });
  const editableKey = updateRegionsForm.watch('id');
  const inputtedName = updateRegionsForm.watch('name');
  const inputtedIdexxNumber = updateRegionsForm.watch('idexxAccountNumber');
  const inputtedZoetisNumber = updateRegionsForm.watch('zoetisAccountNumber');

  const setEditableKey = (
    locationId?: string,
    currentRegionName?: string,
    currentIdexxAccountNumber?: string,
    currentZoetisAccountNumber?: string
  ) => {
    updateRegionsForm.reset();
    setRegionName(currentRegionName);
    setIdexxAccountNumber(currentIdexxAccountNumber);
    setZoetisAccountNumber(currentZoetisAccountNumber);
    updateRegionsForm.setValue('id', locationId as string, {
      shouldValidate: true,
    });
  };
  const regionsQuery = useGetRegionsQuery();
  const updateRegionHandler = updateRegionsForm.handleSubmit((formData) => {
    updateRegion({
      variables: {
        input: {
          ...formData,
        },
      },
    });
  });
  const deleteRegionHandler = (id: string) => {
    deleteRegion({ variables: { input: { id } } });
  };
  const regions = regionsQuery.data?.getRegions;

  const isLoading = [
    deleteRegionMutation,
    updateRegionMutation,
    regionsQuery,
  ].some(({ loading }) => loading);

  const isNewValues =
    regionName !== inputtedName ||
    idexxAccountNumber !== inputtedIdexxNumber ||
    zoetisAccountNumber !== inputtedZoetisNumber;

  const savingIsDisabled =
    !updateRegionsForm.formState.isValid ||
    !updateRegionsForm.formState.isDirty ||
    !isNewValues;

  const regionsTableColumns = useMemo(
    () =>
      getColumns({
        control: updateRegionsForm.control,
        setEditableKey,
        editableKey,
        onSaveChanges: updateRegionHandler,
        savingIsDisabled,
        onDelete: deleteRegionHandler,
        isAllowedToDelete: deleteRegionAllowed,
        isAllowedToEdit: updateRegionAllowed,
        errors: updateRegionsForm.formState.errors,
        isMobile,
      }),
    [
      updateRegionsForm.control,
      inputtedName,
      editableKey,
      updateRegionsForm.formState.isValid,
      updateRegionsForm.formState.isDirty,
      isMobile,
    ]
  );

  return (
    <Row gutter={[0, 20]}>
      {createNewRegionAllowed && (
        <Col span={24}>
          <CreateRegionsForm />
        </Col>
      )}
      <Col span={24} className={styles.tableWrapper}>
        <Spin spinning={isLoading}>
          <Table
            rowKey={({ id }) => id}
            columns={regionsTableColumns}
            dataSource={regions}
            pagination={pagination.config}
            size="small"
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default RegionsTable;
